'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import { ArrowRight, PlayCircle, Sparkles } from 'lucide-react';
import { ParticleField } from './ParticleField';
import { BullionShowcase } from './BullionShowcase';
import { MagneticButton } from '@/components/shared/MagneticButton';
import { Button } from '@/components/ui/button';
import { SITE, STATS } from '@/lib/constants';
import { formatNumberES } from '@/lib/format';

export function HeroSection() {
  return (
    <section className="relative isolate overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div
          className="absolute inset-0 opacity-70"
          style={{
            background:
              'radial-gradient(60% 50% at 75% 35%, rgba(201,169,97,0.18) 0%, transparent 70%), radial-gradient(50% 40% at 15% 60%, rgba(24,22,20,0.65) 0%, transparent 70%)',
          }}
        />
        <ParticleField count={70} />
      </div>

      <div className="container-page grid items-center gap-12 py-20 sm:py-24 lg:grid-cols-12 lg:gap-16 lg:py-32">
        <div className="space-y-8 lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-gold-500/30 bg-gold-500/10 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-gold-300 backdrop-blur"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {SITE.bajada}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.05 }}
            className="heading-display text-pretty text-4xl text-cream sm:text-5xl lg:text-7xl"
          >
            Aprende a invertir en{' '}
            <span className="text-gold-gradient">oro</span> como un profesional.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="max-w-2xl text-lg leading-relaxed text-cream/75 sm:text-xl"
          >
            El curso gratuito de {SITE.parent}.{' '}
            <span className="text-cream">{SITE.subTagline}.</span> Riguroso,
            cero hype, cero presión comercial.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.15 }}
            className="flex flex-wrap gap-4"
          >
            <MagneticButton variant="primary" size="xl" asChild>
              <Link href="/registro" className="group">
                Empezar curso gratis
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </MagneticButton>
            <Button variant="outline" size="xl" asChild>
              <Link href="#programa" className="group">
                <PlayCircle className="h-5 w-5 text-gold-400" />
                Ver programa
              </Link>
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-cream/60"
          >
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-success" /> Sin
              tarjeta
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-success" />{' '}
              Certificado de Oro Express
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-success" /> Acceso
              ilimitado
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-success" /> Cero
              spam
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="grid grid-cols-2 gap-3 pt-2 sm:grid-cols-4 sm:gap-4"
          >
            {STATS.map((s) => (
              <div
                key={s.label}
                className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center backdrop-blur sm:p-4"
              >
                <div className="font-display text-xl font-bold text-gold-gradient sm:text-2xl">
                  {s.prefix ?? ''}
                  {formatNumberES(s.value)}
                  {s.suffix}
                </div>
                <div className="mt-1 text-[11px] leading-snug text-cream/55 sm:text-xs">
                  {s.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="relative flex items-center justify-center lg:col-span-5"
        >
          <BullionShowcase />
        </motion.div>
      </div>
    </section>
  );
}
